<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { storeToRefs } from 'pinia'

// PrimeVue
import { default as PButton } from 'primevue/button'
import { default as PTable } from 'primevue/datatable'
import { default as PColumn } from 'primevue/column'
import { default as PDrawer } from 'primevue/drawer'
import { default as PTag } from 'primevue/tag'
import { default as PProgressSpinner } from 'primevue/progressspinner'
import { default as PMessage } from 'primevue/message'

// Custom components and types
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'
import { useLearningModulesStore } from '@/presentation/stores/learning/learningModules.ts'
import type { LearningModule } from '@/core/domain/learning/module/LearningModule.ts'
import { useLearningModuleAccessUtils } from '@/presentation/composables/learning/useLearningAccessUtils.ts'

// Setup
const router = useRouter()
const toast = useToast()
const learningModulesStore = useLearningModulesStore()

// Store Refs
const { loading, error } = storeToRefs(learningModulesStore)

// Composables
const { canEditModule, canDeleteModule } = useLearningModuleAccessUtils()

// Props
const props = defineProps<{
  learningCourseId: number
}>()

// Local state
const moduleToArchive = ref<LearningModule | null>(null)
const isUpdating = ref(false)
const modulesList = ref<LearningModule[]>([])

// Computed
const isLoading = computed(() => loading.value.updateModule)
const hasError = computed(() => error.value && error.value.hasError)
const errorMessage = computed(() => (error.value?.hasError ? error.value.message : ''))
const sortedModules = computed(() => {
  if (!modulesList.value) return []
  return [...modulesList.value].sort((a, b) => a.order - b.order)
})

// Methods
function createModule() {
  router.push({ name: 'learning-course-add-module', params: { courseId: props.learningCourseId } })
}

async function archiveModule() {
  if (!moduleToArchive.value) return

  isUpdating.value = true
  try {
    await learningModulesStore.updateModuleStatus(
      LearningStatus.Values.ARCHIVED,
      moduleToArchive.value.id,
      props.learningCourseId
    )

    toast.add({
      severity: 'success',
      summary: 'Module archivé',
      detail: `Le module "${moduleToArchive.value.title}" a été archivé avec succès`,
      life: 3000
    })

    moduleToArchive.value = null
  } catch (error) {
    console.error("Erreur lors de l'archivage du module:", error)
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: "Une erreur est survenue lors de l'archivage du module",
      life: 5000
    })
  } finally {
    isUpdating.value = false
  }
}

onMounted(async () => {
  modulesList.value = await learningModulesStore.fetchAllModules(props.learningCourseId, {
    withTask: true,
    withGrains: true,
    status: [LearningStatus.Values.ACTIVE, LearningStatus.Values.DRAFT]
  })
})
</script>

<template>
  <c-base-block title="Liste des modules" class="w-full">
    <div class="mb-4 flex items-center justify-between">
      <p-button
        @click="createModule()"
        class="ml-auto"
        label="Nouveau module"
        icon="pi pi-plus"
        size="small"
        outlined
        severity="primary"
      />
    </div>

    <!-- Message d'erreur -->
    <p-message v-if="hasError" severity="error" class="mb-4 w-full">
      {{ errorMessage }}
    </p-message>

    <p-table :value="sortedModules" :loading="isLoading" stripedRows>
      <template #empty>
        <div class="align-center text-muted-color flex w-full justify-center p-8">
          Aucun module n'existe
        </div>
      </template>

      <p-column
        field="title"
        header="Titre du module"
        style="white-space: nowrap; text-overflow: ellipsis; overflow: hidden"
      />

      <p-column
        field="task"
        header="Tâche liée"
        style="white-space: nowrap; text-overflow: ellipsis; overflow: hidden"
      >
        <template #body="{ data }">
          <span class="block w-full overflow-hidden text-ellipsis">{{
            data.task?.title || 'Aucune tâche'
          }}</span>
        </template>
      </p-column>

      <p-column
        field="learningGrains"
        header="Grain"
        style="white-space: nowrap; text-overflow: ellipsis; overflow: hidden"
      >
        <template #body="{ data }">
          <span class="block w-full overflow-hidden text-ellipsis">
            {{ data.learningGrains?.length || 0 }}
          </span>
        </template>
      </p-column>

      <p-column field="status" header="Statut" style="width: 8rem">
        <template #body="{ data }">
          <p-tag :severity="LearningStatus.color(data.status)" rounded>
            {{ LearningStatus.label(data.status) }}
          </p-tag>
        </template>
      </p-column>

      <p-column header="Actions" style="width: 8rem">
        <template #body="{ data }">
          <div class="gap-2">
            <router-link
              :to="{
                name: 'learning-course-update-module',
                params: { courseId: learningCourseId, moduleId: data.id }
              }"
            >
              <p-button
                :icon="canEditModule(data) ? 'pi pi-pencil' : 'pi pi-eye'"
                rounded
                text
                v-tooltip="canEditModule(data) ? 'Modifier' : 'Visualiser'"
              />
            </router-link>
            <p-button
              icon="pi pi-inbox"
              rounded
              text
              @click="moduleToArchive = data"
              :disabled="data.status === LearningStatus.Values.ARCHIVED
                || !canDeleteModule(data)"
              v-tooltip="'Archiver'"
            />
          </div>
        </template>
      </p-column>
    </p-table>
  </c-base-block>

  <p-drawer :visible="!!moduleToArchive" position="right" class="w-4/12" :show-close-icon="false">
    <template #header>
      <div class="flex w-full justify-between p-8">
        <p-button
          text
          icon="pi pi-times"
          @click="moduleToArchive = null"
          severity="secondary"
          :disabled="isUpdating"
        />
        <h3 class="text-right">Suppression du module {{ moduleToArchive?.title }}</h3>
      </div>
    </template>

    <template #default>
      <div class="align-center text-color flex h-full w-full flex-col justify-center gap-8 p-8">
        <h5>Êtes-vous sûr.e de vouloir supprimer le module {{ moduleToArchive?.title }} ?</h5>
        <p class="text-muted-color">
          Attention, cette action est irréversible. Si vous cliquez sur valider, votre module sera
          définitivement supprimé.
        </p>

        <div v-if="isUpdating" class="mb-4 flex justify-center">
          <p-progress-spinner style="width: 50px; height: 50px" />
        </div>

        <p-message v-if="hasError" severity="error" class="mb-4 w-full">
          {{ errorMessage }}
        </p-message>

        <p-button
          label="Valider la suppression"
          class="mt-auto justify-self-end"
          @click="archiveModule"
          :disabled="isUpdating"
          :loading="isUpdating"
        ></p-button>
      </div>
    </template>
  </p-drawer>
</template>
